import loadable from "@loadable/component";
import { ConsoleLog, ModalProps } from "@inntechcorp/it-design";
import { GetLocalState } from "Connectivity/LocalStorage";
import ModalRootWrapper from "Modals/index";
import { WebContextProps } from "Types/WebContextProps";

const Disconnect                    = loadable<any>(() => import("Modals/disconnect/index"));
const SignIn                        = loadable<any>(() => import("Modals/signin/index"));
const SignUp                        = loadable<any>(() => import("Modals/signup/index"));
const ForgotPassword                = loadable<any>(() => import("Modals/forgotpassword/index"));
const Promotion                     = loadable<any>(() => import("Modals/promotion/index"));
const GameHistoryDetail             = loadable<any>(() => import("Modals/gamehistorydetail/index"));
const FreeSpinGames                 = loadable<any>(() => import("Modals/freespingames/index"));
const JackpotGames                  = loadable<any>(() => import("Modals/jackpotgames/index"));
const JackpotWinners                = loadable<any>(() => import("Modals/jackpotwinners/index"));
const MedalDetail                   = loadable<any>(() => import("Modals/medaldetail/index"));
const VIPLevelSystem                = loadable<any>(() => import("Modals/viplevelsystem/index"));
const DepositMethod                 = loadable<any>(() => import("Modals/depositmethod/index"));
const WithdrawMethod                = loadable<any>(() => import("Modals/withdrawmethod/index"));
const EditWithdrawAccount           = loadable<any>(() => import("Modals/editwithdrawaccount/index"));
const AddWithdrawAccount            = loadable<any>(() => import("Modals/addwithdrawaccount/index"));
const Proceed                       = loadable<any>(() => import("Modals/proceed/index"));
const IFrameContent                 = loadable<any>(() => import("Modals/iframecontent/index"));

const getModalByName = (name:string) => {

  if ( name === "disconnect" )          return Disconnect;
  if ( name === "signin" )              return SignIn;
  if ( name === "signup" )              return SignUp;
  if ( name === "forgotpassword" )      return ForgotPassword;
  if ( name === "promotion" )           return Promotion;
  if ( name === "gamehistorydetail" )   return GameHistoryDetail;
  if ( name === "freespingames" )       return FreeSpinGames;
  if ( name === "jackpotgames" )        return JackpotGames;
  if ( name === "jackpotwinners" )      return JackpotWinners;
  if ( name === "medaldetail" )         return MedalDetail;
  if ( name === "viplevelsystem" )      return VIPLevelSystem;
  if ( name === "depositmethod" )       return DepositMethod;
  if ( name === "withdrawmethod" )      return WithdrawMethod;
  if ( name === "editwithdrawaccount" ) return EditWithdrawAccount;
  if ( name === "addwithdrawaccount" )  return AddWithdrawAccount;
  if ( name === "proceed" )             return Proceed;
  if ( name === "iframecontent" )       return IFrameContent;
  
  return null;

}

/**
 * 
 * Modal sınıfı `ITDContext` içerisindeki `ModalManager` ile rahatlıkla açılabilir
 * fakat bu fonksiyon açılacak modal'ı config dosyasındaki değerlere göre kontrol
 * eder ve modal'ı bulursa modal'ın açılması için gerekli olan objeyi oluşturur.
 * 
 * Modal yüklendikten sonra `Content` değişkenini kullanmak için `React.cloneElement`
 * fonksiyonu kullanılmalıdır.
 * 
 * Ayrıca modal'lar `WebContext` sınıfına `React.Portal` üzerinde olduklarından 
 * ulaşamazlar. Eğer açılacak modal bir login dönüşüne sahipse `WebContext` sınıfı
 * `changeLogin` fonksiyonunu modal'ın prop'una tanımlar. Böylece kullanıcı login
 * veya kayıt olunca `changeLogin` fonksiyonunu çağırabilir olur.
 * 
 * @param {*} name Açılacak modal'ın ismi. Bu isim config'te tanımlanmış
 *            modalları bulmak için kullanılır.
 * @param {*} data Modal'ın props'u. Açılacak modal hazır bir içeriğe sahip
 *            değilse (örnek promosyon detay) bu bilgi buradan istenilen
 *            şekilde geçilebilir.
 * @param {*} changeLogin WebContext sınıfınınının login olan kullanıcının
 *            bilgisini kayıt eden fonksiyon. 
 * 
 * @returns Modal'ın açılması için hazırlanmış objeyi döndürür.
 * 
 */
const ModalData = (
  name          : string,
  data          : ModalProps,
  context       : WebContextProps
) => {

  // Config'ten gelen modal dataları.
  const modals = GetLocalState("modals");

  if ( !modals ) {
    ConsoleLog.double("🔴 Inn.tech Web Error [Modal Data]", "Can't find the modals session state!", "error");
    return;
  }

  if ( modals.hasOwnProperty(name) ) {
  
    // for mobile
    if ( !data.hasOwnProperty("outerPadding") ) {
      data.outerPadding = "50px 0";
    }

    // Modal dışında tıklanıldığında kapatılır olup
    // olmadığını belirler.
    if ( modals[name].hasOwnProperty("closeOnClickOutside") ) {
      data.closeOnClickOutside = modals[name].closeOnClickOutside;
    }
    
    // Disconnect modalı gibi ESC tuşu ile bile kapanmaması
    // gereken sabit modallar için ESC tuşu ile kapatmayı
    // iptal etme.
    if ( modals[name].hasOwnProperty("preventESC") ) {
      data.preventESC = modals[name].preventESC;
    }

    // Modal'da OK tuşunu göstermez.
    if ( modals[name].hasOwnProperty("showOkButton") ) {
      data.showOkButton = modals[name].showOkButton;
    }

    // Modal'da Cancel tuşunu göstermez.
    if ( modals[name].hasOwnProperty("showCancelButton") ) {
      data.showCancelButton = modals[name].showCancelButton;
    }

    // Modal'da kapat tuşunu göstermez.
    if ( modals[name].hasOwnProperty("showCloseButton") ) {
      data.showCloseButton = modals[name].showCloseButton;
    }

    // 
    if ( modals[name].hasOwnProperty("useRef") ) {
      data.useRef = modals[name].useRef;
    }

    // Bazı modal'lar extra custom proplara ihtiyaç duyabiliyor.
    // Misal Reset Password'ün input göstermesi veya Canlı Destek'e
    // bağlan demesi gibi custom propları ekler.
    if ( modals[name].hasOwnProperty("props") ) {
      data.customProps = {
        ...data.customProps,
        ...modals[name].props
      }
    }

    /**
     * ModalManager sınıfı içerisinde React'in ulaşamadığı
     * Portal'a bilgi geçmek için kullanılan context yapısı bulunur.
     * Buraya kendi context bilgilerimizi ulaşabilmek için iletiriz.
     */
    data.context = context;

    // Yüklenecek modal block'unun adresi.
    if ( !data.content ) {
      
      const modal = getModalByName(name);
      
      if ( !modal ) {
        throw new Error("INN Modal Error: There is no modal registered with this name '" + name + "'");
      }

      data.customProps = {
        ...data.customProps,
        modal         : modal
      }

      data.content = (
        <ModalRootWrapper />
      )

    }

  }
  else {
    ConsoleLog.double("🔴 Inn.tech Web Error [Modal Data]", "Can't find the modal in modals array, name: " + name, "error");
    return;
  }
  
  return data;

}

export default ModalData;